import PlayerKDPeriodSelect from '@/components/PlayerKDPeriodSelect';
import { useBackendApi } from '@/hooks/fetch';
import { PlayerModalRefType } from '@/hooks/playerModal';
import { formatPlayerKD, playerKDColumns } from '@/lib/playerKD';
import { cn } from '@/lib/utils';
import { PlayerKDPeriod, PlayerKDResponse } from '@shared/playerKD';
import { useEffect, useState } from 'react';
import { startAutoRefresh } from '@/lib/autoRefresh';

export default function PlayerKDCard({ playerRef }: { playerRef: PlayerModalRefType }) {
    const [period, setPeriod] = useState<PlayerKDPeriod>('alltime');
    const [result, setResult] = useState<PlayerKDResponse>();
    const [loading, setLoading] = useState(true);
    const api = useBackendApi<PlayerKDResponse>({ method: 'GET', path: '/player/kd', abortOnUnmount: true });

    useEffect(() => {
        let active = true;
        setLoading(true);
        setResult(undefined);
        const stop = startAutoRefresh(async () => {
            try {
                const data = await api({ queryParams: { ...playerRef, kdPeriod: period } });
                if (active) setResult(data ?? { stats: null, period, error: 'Não foi possível consultar o KD.' });
            } catch {
                if (active) setResult({ stats: null, period, error: 'Não foi possível consultar o KD.' });
            } finally {
                if (active) setLoading(false);
            }
        });
        return () => { active = false; stop(); };
    }, [playerRef, period]);

    const stats = result?.stats ?? null;
    return <div className="my-3 rounded-lg border bg-card p-3">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <PlayerKDPeriodSelect value={period} onChange={setPeriod} />
        </div>
        <dl className="grid grid-cols-4 gap-3" aria-busy={loading}>
            {playerKDColumns.map(column => <div key={column.key}>
                <dt className="text-xs font-medium text-muted-foreground"
                    title={column.key === 'hs' ? 'HS% das kills com informação de headshot; o histórico antigo não tinha esse registo.' : undefined}>{column.label}</dt>
                <dd className={cn('mt-1 text-base tabular-nums', column.key === 'kd' && stats
                    && (stats.kd < 1 ? 'text-destructive-inline' : 'text-success-inline'))}>
                    {loading ? '…' : formatPlayerKD(stats, column.key)}
                </dd>
            </div>)}
        </dl>
        {!loading && !stats && <p className="mt-2 text-xs text-muted-foreground" role="status">
            {result?.error ?? 'KD indisponível para este jogador (sem identificador Steam).'}
        </p>}
    </div>;
}
